import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackNavigation, trackFeatureInteraction } from '../../utils/analytics';
import { trackPhoneClick, trackEmailClick } from '../../utils/googleTagTracking';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Startseite', path: '/' },
    { name: 'Kapitalanlage', path: '/kapitalanlage' },
    { name: 'Über uns', path: '/uber-uns' },
    { name: 'Einzugsgebiete', path: '/einzugsgebiete' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: '/blog' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <motion.header 
      className={`bg-white shadow-lg sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-1 shadow-xl' : 'py-2'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mx-auto px-4 max-w-7xl">
        <div className="flex justify-between items-center h-16">
          <motion.div 
            className="flex items-center space-x-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link 
              to="/" 
              className="flex items-center space-x-2 group"
              onClick={() => trackNavigation('Seidels Immobilien Logo', '/', 'Header')}
            >
              <motion.img 
                src="/logo192.png" 
                alt="Seidels Immobilien Berlin Brandenburg - Immobilien verkaufen Kapitalanlage Makler" 
                className="w-8 h-8 object-contain"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              <motion.span 
                className="text-lg md:text-xl font-heading font-bold text-primary-600 group-hover:text-primary-700 transition-colors"
                whileHover={{ scale: 1.05 }}
              >
                Seidels Immobilien
              </motion.span>
            </Link>
          </motion.div>

          <motion.nav 
            className="hidden md:flex items-center space-x-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`relative text-sm font-medium transition-colors duration-200 py-2 px-1 ${
                    isActive(item.path)
                      ? 'text-primary-600'
                      : 'text-secondary-700 hover:text-primary-600'
                  }`}
                  onClick={() => {
                    trackNavigation(item.name, item.path, 'Header Desktop');
                    if (item.path === '/kontakt') {
                      try {
                        const c = localStorage.getItem('cookieConsent');
                        const consent = c ? JSON.parse(c) : null;
                        if (consent && consent.marketing && (window as any).fbq) {
                          (window as any).fbq('track', 'Contact');
                        }
                      } catch {}
                    }
                  }}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"
                      layoutId="activeTab"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </motion.nav>

          <motion.div 
            className="hidden lg:flex items-center space-x-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <motion.a
              href="tel:017632135694"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors bg-primary-50 px-3 py-2 rounded-lg text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                trackFeatureInteraction('Contact', 'Phone Call', { location: 'Header', phoneNumber: '0176 321 35 694' });
                try {
                  const c = localStorage.getItem('cookieConsent');
                  const consent = c ? JSON.parse(c) : null;
                  if (consent && consent.marketing && (window as any).fbq) {
                    (window as any).fbq('track', 'Contact');
                  }
                } catch {}
                trackPhoneClick('017632135694');
              }}
            >
              <Phone size={16} />
              <span className="font-medium">0176 321 35 694</span>
            </motion.a>
            <motion.a
              href="mailto:oscar@seidelsimmobilien.de"
              className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors bg-primary-50 px-3 py-2 rounded-lg text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                trackFeatureInteraction('Contact', 'Email', { location: 'Header', email: 'oscar@seidelsimmobilien.de' });
                try {
                  const c = localStorage.getItem('cookieConsent');
                  const consent = c ? JSON.parse(c) : null;
                  if (consent && consent.marketing && (window as any).fbq) {
                    (window as any).fbq('track', 'Contact');
                  }
                } catch {}
                trackEmailClick('oscar@seidelsimmobilien.de');
              }}
            >
              <Mail size={16} />
              <span className="font-medium">Email</span>
            </motion.a>
          </motion.div>

          <motion.button
            className="md:hidden p-2 rounded-lg hover:bg-secondary-100 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              trackFeatureInteraction('Menu', isMenuOpen ? 'Close' : 'Open', { location: 'Header Mobile' });
            }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isMenuOpen}
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={24} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu size={24} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="md:hidden bg-white border-t border-secondary-200 overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            >
              <motion.nav 
                className="flex flex-col py-4"
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <Link
                      to={item.path}
                      className={`block py-3 px-4 text-base font-medium transition-colors duration-200 min-h-[48px] flex items-center ${
                        isActive(item.path)
                          ? 'text-primary-600 bg-primary-50 border-l-4 border-primary-600'
                          : 'text-secondary-700 hover:text-primary-600 hover:bg-secondary-50'
                      }`}
                      onClick={() => {
                        setIsMenuOpen(false);
                        trackNavigation(item.name, item.path, 'Header Mobile Menu');
                        if (item.path === '/kontakt') {
                          try {
                            const c = localStorage.getItem('cookieConsent');
                            const consent = c ? JSON.parse(c) : null;
                            if (consent && consent.marketing && (window as any).fbq) {
                              (window as any).fbq('track', 'Contact');
                            }
                          } catch {}
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div 
                  className="flex flex-col space-y-3 pt-4 px-4 border-t border-secondary-200 mt-4"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <a
                    href="tel:017632135694"
                    className="flex items-center space-x-3 text-primary-600 hover:text-primary-700 transition-colors bg-primary-50 p-3 rounded-lg min-h-[48px]"
                    onClick={() => {
                      trackFeatureInteraction('Contact', 'Phone Call', { location: 'Header Mobile Menu', phoneNumber: '0176 321 35 694' });
                      try {
                        const c = localStorage.getItem('cookieConsent');
                        const consent = c ? JSON.parse(c) : null;
                        if (consent && consent.marketing && (window as any).fbq) {
                          (window as any).fbq('track', 'Contact');
                        }
                      } catch {}
                    }}
                  >
                    <Phone size={20} />
                    <span className="font-medium">0176 321 35 694</span>
                  </a>
                  <a
                    href="mailto:oscar@seidelsimmobilien.de"
                    className="flex items-center space-x-3 text-primary-600 hover:text-primary-700 transition-colors bg-primary-50 p-3 rounded-lg min-h-[48px]"
                    onClick={() => {
                      trackFeatureInteraction('Contact', 'Email', { location: 'Header Mobile Menu', email: 'oscar@seidelsimmobilien.de' });
                      try {
                        const c = localStorage.getItem('cookieConsent');
                        const consent = c ? JSON.parse(c) : null;
                        if (consent && consent.marketing && (window as any).fbq) {
                          (window as any).fbq('track', 'Contact');
                        }
                      } catch {}
                    }}
                  >
                    <Mail size={20} />
                    <span className="font-medium">oscar@seidelsimmobilien.de</span>
                  </a>
                </motion.div>
              </motion.nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
